/**
 * EditDevice Component
 * 
 * Dialog component for editing registered device records.
 * Allows updating device-specific information without modifying the template.
 * 
 * Editable fields:
 * - Device name
 * - MAC address
 * - Firmware version
 * - Status (active/inactive)
 * - Notes
 * - Installation date
 * 
 * Non-editable fields (from template):
 * - Device type
 * - Operating system
 * - Sensors/I/O configuration
 * - Device ID (generated, should not change)
 * 
 * @param {Object} device - Device data to edit
 * @param {boolean} isDialogOpen - Controls dialog visibility
 * @param {Function} handleClose - Function to close the dialog
 */
import React, { useEffect, useState } from "react";

import { doc, updateDoc, query, collection, where, getDocs } from "firebase/firestore";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Snackbar,
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Chip,
} from "@mui/material";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import moment from "moment";

import { firebaseStore } from "../../../../components/firebase/firestore";
import { primaryColorTextField } from "../../../../components/theme/globalStyles";

function EditDevice({ device, isDialogOpen, handleClose }: any) {
  // UI state
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Editable device information
  const [deviceName, setDeviceName] = useState<string>("");
  const [macAddress, setMacAddress] = useState<string>("");
  const [firmwareVersion, setFirmwareVersion] = useState<string>("");
  const [status, setStatus] = useState<string>("active");
  const [notes, setNotes] = useState<string>("");
  const [installationDate, setInstallationDate] = useState<number | null>(Date.now());

  /**
   * Initialize form with existing device data when dialog opens
   */
  useEffect(() => {
    if (device && isDialogOpen) {
      setDeviceName(device.name || "");
      setMacAddress(device.macAddress || "");
      setFirmwareVersion(device.firmwareVersion || "");
      setStatus(device.status || "active");
      setNotes(device.notes || "");
      setInstallationDate(device.installationDate || Date.now());
      setErrorMessage("");
    }
  }, [device, isDialogOpen]);

  /**
   * Validate MAC address format
   * Accepts formats: XX:XX:XX:XX:XX:XX or XX-XX-XX-XX-XX-XX
   */
  const isValidMacAddress = (mac: string): boolean => {
    const macRegex = /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/;
    return macRegex.test(mac);
  };

  /**
   * Check if MAC address already exists in database (excluding current device)
   */
  const checkMacAddressExists = async (mac: string): Promise<boolean> => {
    const q = query(
      collection(firebaseStore, "devices"),
      where("macAddress", "==", mac)
    );
    const querySnapshot = await getDocs(q);
    
    // Check if any document with this MAC exists that isn't the current device
    let exists = false;
    querySnapshot.forEach((doc) => {
      if (doc.id !== device.id) {
        exists = true;
      }
    });
    return exists;
  };

  /**
   * Validate form before submission
   */
  const validateForm = (): boolean => {
    if (!deviceName.trim()) {
      setErrorMessage("Device name is required");
      return false;
    }

    if (!macAddress.trim()) {
      setErrorMessage("MAC address is required");
      return false;
    }

    if (!isValidMacAddress(macAddress)) {
      setErrorMessage(
        "Invalid MAC address format. Use XX:XX:XX:XX:XX:XX or XX-XX-XX-XX-XX-XX"
      );
      return false;
    }

    return true;
  };

  /**
   * Save updated device information to Firestore
   */
  const handleSave = async () => {
    setErrorMessage("");

    if (!validateForm()) return;

    // Check if MAC address already exists (for a different device)
    setLoading(true);
    const macExists = await checkMacAddressExists(macAddress);

    if (macExists) {
      setErrorMessage("A different device with this MAC address already exists");
      setLoading(false);
      return;
    }

    try {
      const deviceRef = doc(firebaseStore, "devices", device.id);
      
      await updateDoc(deviceRef, {
        name: deviceName,
        macAddress: macAddress,
        firmwareVersion: firmwareVersion,
        status: status,
        notes: notes,
        installationDate: installationDate,
        updatedAt: Date.now(),
      });

      setSnackbarMessage("Device updated successfully");
      setSnackbarOpen(true);
      
      // Close dialog after brief delay
      setTimeout(() => {
        handleClose();
      }, 1000);
    } catch (error) {
      console.error("Error updating device:", error);
      setErrorMessage("Failed to update device. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Reset form to original device data
   */
  const handleReset = () => {
    setDeviceName(device.name || "");
    setMacAddress(device.macAddress || "");
    setFirmwareVersion(device.firmwareVersion || "");
    setStatus(device.status || "active");
    setNotes(device.notes || "");
    setInstallationDate(device.installationDate || Date.now());
    setErrorMessage("");
  };

  return (
    <>
      <Dialog
        open={isDialogOpen}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            padding: 1,
          }
        }}
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight={600}>
            Edit Device
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Update device configuration and information
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, paddingTop: 2 }}>
            {/* Read-only Device Info */}
            <Box sx={{ 
              backgroundColor: "#f5f5f5", 
              padding: 2, 
              borderRadius: 1,
              border: "1px solid #e0e0e0" 
            }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                Device ID (Read-only)
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  fontFamily: "monospace", 
                  fontSize: "0.85rem",
                  marginTop: 0.5,
                  wordBreak: "break-all"
                }}
              >
                {device?.deviceId}
              </Typography>
              <Box sx={{ display: "flex", gap: 1, marginTop: 1 }}>
                <Chip label={device?.deviceEnumType} size="small" />
                <Chip label={device?.os} size="small" color="primary" />
              </Box>
            </Box>

            {/* Device Name */}
            <TextField
              label="Device Name *"
              value={deviceName}
              onChange={(e) => setDeviceName(e.target.value)}
              fullWidth
              sx={primaryColorTextField}
              helperText="A friendly name for this device"
            />

            {/* MAC Address */}
            <TextField
              label="MAC Address *"
              value={macAddress}
              onChange={(e) => setMacAddress(e.target.value.toUpperCase())}
              fullWidth
              sx={primaryColorTextField}
              placeholder="XX:XX:XX:XX:XX:XX"
              helperText="The device's unique hardware address"
              error={macAddress.length > 0 && !isValidMacAddress(macAddress)}
            />

            {/* Status */}
            <FormControl fullWidth sx={primaryColorTextField}>
              <InputLabel>Status</InputLabel>
              <Select
                value={status}
                label="Status"
                onChange={(e) => setStatus(e.target.value)}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>

            {/* Firmware Version */}
            <TextField
              label="Firmware Version"
              value={firmwareVersion}
              onChange={(e) => setFirmwareVersion(e.target.value)}
              fullWidth
              sx={primaryColorTextField}
              placeholder="e.g., v1.2.3"
            />

            {/* Installation Date */}
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                label="Installation Date"
                value={moment(installationDate)}
                onChange={(newValue) => {
                  if (newValue) {
                    //@ts-ignore
                    setInstallationDate(newValue.valueOf());
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    sx={primaryColorTextField}
                  />
                )}
              />
            </LocalizationProvider>

            {/* Notes */}
            <TextField
              label="Notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              fullWidth
              multiline
              rows={4}
              sx={primaryColorTextField}
              helperText="Any additional information about this device"
            />

            {/* Error Message */}
            {errorMessage && (
              <Alert severity="error" onClose={() => setErrorMessage("")}>
                {errorMessage}
              </Alert>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ padding: 2, paddingTop: 0 }}>
          <Button onClick={handleReset} variant="outlined" disabled={loading}>
            Reset
          </Button>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={loading}
            sx={{
              backgroundColor: "#44a99a",
              "&:hover": {
                backgroundColor: "#3a8f82",
              }
            }}
          >
            {loading ? "Saving..." : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </>
  );
}

export default EditDevice;
